'use client';
import Link from 'next/link';
import { IRepository, IResourceVersion } from '@/types';
import * as Styled from './styled';
import * as Icon from '@/ui/Icon';
import CopyTextControl from '@/ui/CopyTextControl';
import { Row } from '@/ui/primatives';

type PathLink = { name: string; href: string };

const BreadCrumbItem: React.FC<{ pathLink: PathLink; activeEntry?: PathLink; pathSize: number; index: number }> = ({
  pathLink,
  activeEntry,
  pathSize,
  index,
}) => {
  const position = index + 1;
  return (
    <>
      <Styled.BreadCrumbItem key={pathLink.href}>
        {(index > 0 && (position < pathSize || activeEntry?.name)) ? <span className="seperator">/</span> : null}
        <Link href={pathLink.href}>{decodeURIComponent(pathLink.name)}</Link>
      </Styled.BreadCrumbItem>
    </>
  );
};

const BreadCrumbs: React.FC<{
  repository: IRepository;
  pathResource?: IResourceVersion | null;
  showIcon?: boolean;
  showCopy?: boolean;
}> = ({ repository, pathResource, showIcon = true, showCopy = false }) => {
  const pathItems = pathResource?.path?.split('/') || [];

  let pathLinks: PathLink[] = [];
  let currentPath = `/${repository.namespace}/${repository.name}/dir/${pathResource?.version}`;
  pathLinks.push({ name: repository.name, href: currentPath });

  pathItems.forEach((path) => {
    currentPath = `${currentPath}/${path}`;
    if (path != '.') {
      pathLinks.push({ name: path, href: currentPath });
    }
  });

  const activeEntry = pathLinks.pop();

  return (
    <Styled.BreadCrumbs>
      {showIcon && <Icon.Server />}
      <Styled.BreadCrumbItems>
        {pathLinks.map((pathLink, index) => (
          <BreadCrumbItem
            key={pathLink.href}
            pathLink={pathLink}
            activeEntry={activeEntry}
            pathSize={pathLinks.length}
            index={index}
          />
        ))}
        <Row gap={0} alignItems="center">
          <span className="seperator">/</span>
          <Styled.FileName>{decodeURIComponent(activeEntry?.name || '')}</Styled.FileName>
          {showCopy && (
            <CopyTextControl
              id="resource-path"
              value={pathResource?.path || ''}
              event={{
                name: 'copied_resource_path',
                payload: { namespace: repository.namespace, repository: repository.name, path: pathResource?.path },
              }}
            />
          )}
        </Row>
      </Styled.BreadCrumbItems>
    </Styled.BreadCrumbs>
  );
};

export default BreadCrumbs;
